/**
 * IGRA-48 — распределение трафика между лендингом A (текущий), лендингом B
 * (структура ivi.ru/subscribe, крупная типографика, красный CTA) и лендингом C
 * (смыслы и золотая палитра A в подаче B).
 *
 * Правила:
 * 1. `?lp=a` / `?lp=b` / `?lp=c` — принудительный выбор. Нужен для QA и для
 *    ссылок в рекламных кампаниях, привязанных к конкретному варианту. Выбор
 *    запоминается, чтобы человек не увидел разные лендинги при возврате.
 * 2. Иначе — сохранённый вариант из localStorage. Один человек за всё время
 *    теста видит ОДИН лендинг: иначе замер сравнивает не варианты, а случайные
 *    показы, и результат теста ничего не значит.
 * 3. Иначе — жребий 50/50 МЕЖДУ A И B. Вариант C жребием не выпадает никогда:
 *    сейчас идёт замер A против B, и втихую превратить его в трёхсторонний
 *    сплит — значит обнулить уже собранные данные. C открывается только явно —
 *    по `?lp=c` или прямой ссылке `/lp-c`.
 *
 * Fail-safe: любая ошибка (приватный режим, отключённый localStorage, SSR) →
 * вариант A. Показать проверенный лендинг всегда безопаснее, чем упасть.
 */

export type LandingVariant = 'a' | 'b' | 'c'

/** Варианты, между которыми делится трафик жребием (C сюда не входит). */
const ROLLED_VARIANTS = ['a', 'b'] as const

export const LANDING_VARIANT_STORAGE_KEY = 'igra.lp'
export const LANDING_VARIANT_QUERY_KEY = 'lp'

const isVariant = (value: unknown): value is LandingVariant =>
  value === 'a' || value === 'b' || value === 'c'

const read = (): LandingVariant | null => {
  try {
    const stored = window.localStorage.getItem(LANDING_VARIANT_STORAGE_KEY)
    return isVariant(stored) ? stored : null
  } catch {
    return null
  }
}

const write = (variant: LandingVariant): void => {
  try {
    window.localStorage.setItem(LANDING_VARIANT_STORAGE_KEY, variant)
  } catch {
    // Приватный режим/выключенное хранилище — вариант просто не запомнится.
    // Это хуже для чистоты замера, но не ломает показ.
  }
}

/**
 * @param forced значение `?lp` из query (route.query.lp), если оно есть
 * @param roll   источник жребия — инъекция для тестов, по умолчанию Math.random
 */
export function resolveLandingVariant(
  forced?: unknown,
  roll: () => number = Math.random,
): LandingVariant {
  if (isVariant(forced)) {
    write(forced)
    return forced
  }

  if (typeof window === 'undefined') {
    return 'a'
  }

  const stored = read()
  if (stored !== null) {
    return stored
  }

  const assigned: LandingVariant =
    roll() < 0.5 ? ROLLED_VARIANTS[0] : ROLLED_VARIANTS[1]
  write(assigned)

  return assigned
}
